import { useCallback, useMemo } from "react"

import {
  useOrganizationUsersTransport,
  type OrganizationUser,
} from "../transport"

const ULID_PATTERN = /^[0-9A-HJKMNP-TV-Z]{26}$/i

/** Rendered in place of a creator when an asset records none. */
export const NO_CREATOR = "—"
const API_CREATOR_LABEL = "API"

interface CreatorResolver {
  creatorOptions: { value: string | null; label: string; aliases?: string[] }[]
  resolveCreator: (creator: string | null | undefined) => string
  isLoading: boolean
  error: Error | null
  retry: () => Promise<unknown>
}

/** Resolves canonical user IDs without historical identity inference; callers using legacy metadata retain email/API token support until their transport contract is migrated. */
export function useCreatorResolver({
  canonical = false,
} = {}): CreatorResolver {
  const { usersByUlid, isLoading, error, refetch } =
    useOrganizationUsersTransport()

  const usersByEmail = useMemo(() => {
    const byEmail: Record<string, OrganizationUser> = {}
    for (const user of Object.values(usersByUlid)) {
      if (user.email) {
        byEmail[user.email.toLowerCase()] = user
      }
    }
    return byEmail
  }, [usersByUlid])

  const resolveCreator = useCallback(
    (creator: string | null | undefined): string => {
      if (!creator) {
        return NO_CREATOR
      }
      const matched =
        usersByUlid[creator] ??
        (canonical ? undefined : usersByEmail[creator.toLowerCase()])
      if (matched) {
        return matched.displayName || matched.email || creator
      }
      if (!canonical && creator.toLowerCase() === "api") {
        return API_CREATOR_LABEL
      }
      if (canonical || ULID_PATTERN.test(creator)) {
        if (error) {
          return "Unavailable"
        }
        return isLoading ? "Loading..." : "Unknown"
      }
      return creator
    },
    [usersByUlid, usersByEmail, isLoading, error, canonical],
  )

  const creatorOptions = useMemo(
    () => [
      ...Object.entries(usersByUlid).map(([id, user]) => ({
        value: id,
        label: user.displayName || user.email || id,
        aliases: canonical
          ? undefined
          : [user.email, user.clerkId].filter(Boolean),
      })),
      ...(!canonical ? [{ value: "api", label: API_CREATOR_LABEL }] : []),
      { value: null, label: "Not set" },
    ],
    [usersByUlid, canonical],
  )
  return useMemo(
    () => ({
      resolveCreator,
      isLoading,
      creatorOptions,
      error,
      retry: refetch,
    }),
    [resolveCreator, isLoading, creatorOptions, error, refetch],
  )
}
